import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2
} from '@angular/core';
import {DOCUMENT} from "@angular/common";
import {distinctUntilChanged, filter, map, switchMap, take, takeUntil, tap,} from 'rxjs/operators';
import {fromEvent, Subject} from 'rxjs';
import {TileDataService} from "../services/tile_data.service";
import {ColumnFormats} from "../services/table-utils.service";
import {CustomDialogService} from "../services/custom-dialog.service";

@Directive({
    selector: '[resizable]',
    standalone: true
})
export class ColumnResizeDirective implements OnInit, OnDestroy {
    private readonly onDestroy = new Subject<void>();

    @Input('resizable') formats: Partial<ColumnFormats>;
    @Input('constrainWidth') constrain_width: boolean;
    @Input('alwaysOn') alwaysOn: boolean = false;
    @Input('showModal') showModal: boolean = true;
    @Output() dragComplete: EventEmitter<void> = new EventEmitter();
    editing = false;
    handle: Element;
    dragging = false;
    limitsRemoved = false;

    @HostListener('click', ['$event'])
    clickEvent(event) {
        if (!this.editing || !this.formats.allow_resize || this.dragging) return;
        event.preventDefault();
        event.stopPropagation();
        if (this.showModal) {
            this.openModal(event);
        }
    }

    @HostListener('mouseup')
    mouseUp(): void {
        this.dragComplete.next();
    }

    @Output()
    readonly resizable = fromEvent<MouseEvent>(
        this.elementRef.nativeElement,
        'mousedown'
    ).pipe(
        filter(event => event['which'] === 1 && this.editing),
        tap(e => {
            e.preventDefault();
            e.stopPropagation();
        }),
        switchMap(() => {
            const {width, right} = this.elementRef.nativeElement
                .closest('th')!
                .getBoundingClientRect();

            return fromEvent<MouseEvent>(this.documentRef, 'mousemove').pipe(
                map(({clientX}) => {
                    if (!this.limitsRemoved) {
                        this.removeLimits();
                        this.limitsRemoved = true;
                    }
                    this.dragging = true;
                    return width + clientX - right
                }),
                tap(() => this.setLimits(true)),
                distinctUntilChanged(),
                takeUntil((fromEvent(this.documentRef, 'mouseup') ||
                        fromEvent(this.elementRef.nativeElement.closest('thead'), 'mouseout')).pipe(
                    )
                )
            );
        }),
        tap(() => {
            this.dragging = false;
            this.limitsRemoved = false;
        }),
        takeUntil(this.onDestroy)
    );

    constructor(
        @Inject(DOCUMENT) private readonly documentRef: Document,
        @Inject(ElementRef)
        private readonly elementRef: ElementRef<HTMLElement>,
        private renderer: Renderer2, private tileData: TileDataService,
                private customDialogService: CustomDialogService,
    ) {
        this.handle = this.renderer.createElement('div');
        this.renderer.addClass(this.handle, 'resize-handle');
        const grip = this.renderer.createElement('i');
        this.renderer.addClass(grip, 'fas')
        this.renderer.addClass(grip, 'fa-grip-vertical')
        this.renderer.appendChild(this.handle, grip);
    }

    ngOnInit(): void {
        if (this.alwaysOn) {
            this.editing = true;
            this.toggleResizeHandles(true)
        } else {
            this.tileData.editing.pipe(takeUntil(this.onDestroy))
                .subscribe((on: boolean) => {
                    this.editing = on;
                    this.toggleResizeHandles(on)
                })
        }

        this.setLimits(true);
    }

    //TODO Why does mat-table ignore these when resizing another column
    setLimits(on) {
        if (!this.formats || !this.formats.width || (!this.formats.allow_resize && this.editing)) return;
        this.renderer.setStyle(this.elementRef.nativeElement, 'width', this.formats.width + 'px');

        if (this.constrain_width === true) return;
        this.renderer.setStyle(this.elementRef.nativeElement, 'max-width', this.formats.width + 'px');
        this.renderer.setStyle(this.elementRef.nativeElement, 'min-width', this.formats.width + 'px');
    }

    removeLimits() {
        if (!this.formats || !this.formats.width || !this.editing || (!this.formats.allow_resize && this.editing)) return;
        this.renderer.removeStyle(this.elementRef.nativeElement, 'max-width');
        this.renderer.removeStyle(this.elementRef.nativeElement, 'min-width');
    }

    toggleResizeHandles(on = false) {
        /* Just displays and hides the resizing handles on Edit Layout */
        if (this.formats && this.formats.allow_resize !== true) return;
        if (on) {
            this.renderer.removeChild(this.elementRef.nativeElement, this.handle);
            this.renderer.appendChild(this.elementRef.nativeElement, this.handle)
        } else {
            this.renderer.removeChild(this.elementRef.nativeElement, this.handle);
        }
    }

    openModal(event: any) {
        const config = {
            component: 'ColumnWidth',
            parameters: {width: this.formats.width, title: 'Column Width (px)'},
            position: {top: 2, left: 60, max_right: 200},
        }
        const setStyles: (event) => void = (event) => {
            this.formats.width = event;
            this.setLimits(true);
        }
        const panelClass = 'series-summary-menu-dialog';
        this.customDialogService.openCustomDialog(event, config, panelClass, setStyles);
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
